import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AnimatedHeader from './AnimatedHeader';
import RatesTable from './RatesTable';
import DataCollectionModal from './DataCollectionModal';
import SignUp from './SignUp'; 
import FAQSection from './FAQSection';
import { Analytics } from '@vercel/analytics/react';

export default function Home({ scrollTo }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTable, setActiveTable] = useState('mortgage');

  // References to each section
  const headerRef = useRef(null);
  const tableRef = useRef(null);
  const signupRef = useRef(null);
  const faqRef = useRef(null);

  // 1) On mount, if user visits /rates, /signup, or /faq directly, scroll there
  useEffect(() => {
    if (scrollTo === 'rates' && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (scrollTo === 'signup' && signupRef.current) {
      signupRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (scrollTo === 'faq' && faqRef.current) {
      faqRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollTo]);

  // 2) IntersectionObserver to update the URL as you scroll
  useEffect(() => {
    const sections = [
      { ref: headerRef, path: '/' },
      { ref: tableRef, path: '/rates' },
      { ref: signupRef, path: '/signup' },
      { ref: faqRef, path: '/faq' },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        let maxRatioEntry = null;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!maxRatioEntry || entry.intersectionRatio > maxRatioEntry.intersectionRatio) {
              maxRatioEntry = entry;
            }
          }
        }

        if (maxRatioEntry) {
          const matchedSection = sections.find(
            (s) => s.ref.current === maxRatioEntry.target
          );
          if (matchedSection && location.pathname !== matchedSection.path) {
            navigate(matchedSection.path, { replace: true });
          }
        }
      },
      {
        threshold: 0.75,
      }
    );

    sections.forEach((section) => {
      if (section.ref.current) {
        observer.observe(section.ref.current);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section.ref.current) {
          observer.unobserve(section.ref.current);
        }
      });
    };
  }, [location, navigate]);

  // Handle animation completion
  const handleAnimationEnd = () => {
    if (location.pathname === '/') {
      navigate('/rates', { replace: true });
    }
  };

  return (
    <>
      <DataCollectionModal />

      {/* HEADER / ANIMATED SECTION */}
      <section
        ref={headerRef}
        className="header-section"
        style={{ minHeight: '100vh' }}
      >
        <AnimatedHeader onAnimationEnd={handleAnimationEnd} />
      </section>

      {/* RATES TABLE SECTION */}
      <section
        ref={tableRef}
        className="table-section"
        id="rates"
        style={{ minHeight: '100vh', padding: '40px 0' }}
      >
        <div className="floating-buttons">
          <button
            className={activeTable === 'deposit' ? 'active' : ''}
            onClick={() => setActiveTable('deposit')}
          >
            Savings
          </button>
          {/* <button
            className={activeTable === 'fixed-income' ? 'active' : ''}
            onClick={() => setActiveTable('fixed-income')}
          >
            Fixed Income
          </button> */}
          <button
            className={activeTable === 'cds' ? 'active' : ''}
            onClick={() => setActiveTable('cds')}
          >
            CDs(Coming Soon)
          </button>
        </div>
        <RatesTable />
        <Analytics mode="production" />
      </section>

      {/* SIGN-UP SECTION */}
      <section
        ref={signupRef}
        id="signup-section"
        style={{ minHeight: '100vh', padding: '40px 0' }}
      >
        <SignUp />
      </section>

      {/* FAQ SECTION */}
      <section
        ref={faqRef}
        id="faq-section"
        style={{ minHeight: '100vh', padding: '40px 0'}}
      >
        <FAQSection />
      </section>
    </>
  );
}
